import React from 'react'
import { ChevronRight, HardDrive, Plus, Search, UploadCloud, X } from 'lucide-react'
import { Button } from '@iws/admin-ui/button'
import { Input } from '@iws/admin-ui/input'

interface MediaHeaderProps {
  mode: 'page' | 'modal'
  currentFolderId: string | null
  currentFolderName: string
  folderCount: number
  fileCount: number
  searchTerm: string
  canUpload: boolean
  canCreateFolder: boolean
  onBack: () => void
  onClose?: () => void
  onUpload: () => void
  onNewFolder: () => void
  onSearchChange: (term: string) => void
}

export const MediaHeader = ({
  mode,
  currentFolderId,
  currentFolderName,
  folderCount,
  fileCount,
  searchTerm,
  canUpload,
  canCreateFolder,
  onBack,
  onClose,
  onUpload,
  onNewFolder,
  onSearchChange,
}: MediaHeaderProps) => {
  const isModal = mode === 'modal'

  return (
    <div className="z-10 flex shrink-0 flex-col border-b border-border-subtle bg-card">
      {/* Modal title row: title | close */}
      {isModal && (
        <div className="flex items-center justify-between gap-3 px-4 pb-1 pt-4">
          <h2 className="text-heading-md text-ink">Select file</h2>
          {onClose && (
            <Button size="icon" variant="ghost" aria-label="Close" onClick={onClose}>
              <X />
            </Button>
          )}
        </div>
      )}

      {/* Row 1: back+folder | actions */}
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <nav aria-label="Folder path" className="flex min-w-0 items-center gap-1.5">
          <div className="grid size-7 shrink-0 place-items-center rounded-md bg-fill">
            <HardDrive className="size-4 text-ink" />
          </div>
          {currentFolderId ? (
            <>
              <button
                type="button"
                onClick={onBack}
                className="shrink-0 rounded-sm text-sm text-text-2 outline-none transition-colors hover:text-ink hover:underline focus-visible:ring-2 focus-visible:ring-ring"
              >
                All files
              </button>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <p className="truncate text-sm font-semibold text-ink" aria-current="page">
                {currentFolderName}
              </p>
            </>
          ) : (
            <p className="truncate text-sm font-semibold text-ink">All files</p>
          )}
          <p className="ml-1 hidden shrink-0 text-xs tabular-nums text-text-2 sm:block">
            {folderCount} {folderCount === 1 ? 'folder' : 'folders'} · {fileCount} {fileCount === 1 ? 'file' : 'files'}
          </p>
        </nav>

        <div className="flex items-center gap-2">
          {/* Desktop action buttons */}
          <div className="hidden items-center gap-2 sm:flex">
            {canCreateFolder && (
              <Button
                variant="outline"
                onClick={onNewFolder}>
                <Plus />
                New folder
              </Button>
            )}
            {canUpload && (
              <Button
                variant={isModal ? 'secondary' : 'default'}
                disabled={!currentFolderId}
                title={currentFolderId ? undefined : 'Open a folder to upload'}
                onClick={onUpload}>
                <UploadCloud />
                {isModal ? 'Add media' : 'Upload files'}
              </Button>
            )}
          </div>

          {/* Mobile icon-only buttons */}
          <div className="flex items-center gap-1 sm:hidden">
            {canCreateFolder && (
              <Button size="icon" variant="ghost" aria-label="New folder" onClick={onNewFolder}>
                <Plus />
              </Button>
            )}
            {canUpload && (
              <Button size="icon" variant="ghost" aria-label="Upload files" disabled={!currentFolderId} onClick={onUpload}>
                <UploadCloud />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: search bar */}
      <div className="px-4 pb-3">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder={currentFolderId ? `Search in ${currentFolderName}` : 'Search folders'}
            className="pl-8"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Clear search"
              className="absolute right-1 top-1/2 -translate-y-1/2"
              onClick={() => onSearchChange('')}
            >
              <X />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
